import workletUrl from './audioWorkletProcessor.ts?worker&url';
import { setSafariAudioSessionType } from '@mimir/browser/gldraw/browserQuirks';
import type { AudioWorkletRequest, AudioWorkletResponse } from '@mimir/cutter/player/audioWorkletProtocol';
import { WorkletAudioHandler } from '@mimir/cutter/player/workletAudioHandler';

export function createWorkletAudioHandler(): WorkletAudioHandler {
  const handler = new WorkletAudioHandler();
  const context = new AudioContext({ sampleRate: 48000 });

  /*  Safari has a bug where the AudioContext gets tied to the ringer volume instead of
   *  the media volume. The workaround (since iOS 17) is to explicitly set what kind of
   *  audio session it is for the page. If you are reading this and trying to fix an audio
   *  issue for earlier versions the previous workaround was to do this:
   *
   *  var el = document.createElement( 'audio' );
   *  el.src = 'silence.mp3'; // media file with tiny bit of silence
   *  el.play();
   *
   *  .. which of course is horrendous
   */
  setSafariAudioSessionType(navigator, 'playback');

  /**
   *  Vite has special handling for worker source code, but it only matches source that
   *  starts with new "Worker(new URL". Since the AudioWorklet API uses addModule instead
   *  of new Worker we need to trick vite with this dummy Worker class. Try to beat this
   *  hack.
   */
  if (context.sampleRate !== 48000) {
    throw new Error('AudioContext sample rate must be 48000 Hz, but was ' + context.sampleRate);
  }
  if (!context.audioWorklet) {
    throw new Error('AudioContext not supported in this context');
  }

  context.audioWorklet.addModule(workletUrl)
    .then(() =>{
      const audioWorklet = new AudioWorkletNode(context, 'cutterAudioWorkletProcessor', { outputChannelCount: [2] });
      audioWorklet.connect(context.destination);
      audioWorklet.port.onmessage = (msg: MessageEvent<AudioWorkletResponse>) => {
        handler.handleResponse(msg.data);
      };
      handler.initialize(
        context.sampleRate,
        context.outputLatency,
        (request: AudioWorkletRequest) => {
          audioWorklet.port.postMessage(request);
        }
      );
    });

  document.addEventListener('click', () => {
    if (context.state === 'suspended') {
      context.resume();
    }
  });
  document.addEventListener('keydown', () => {
    if (context.state === 'suspended') {
      context.resume();
    }
  });
  return handler;
}

